// People table columns — the domain cells (person identity, role/structure/
// project/group/tag chips, phone, status, relative last-login, plus an actions
// placeholder each tab overrides) rendered inside the common DataTable.
//
// NOTHING is sortable: POST company-users/users takes an orderBy, but its
// accepted keys are not confirmed, and DataTable draws a chevron for any column
// marked sortable whether or not the header can act — a sort that silently does
// nothing is worse than no sort. When the keys land they belong in an enum whose
// members double as these column keys (the SubmissionSortKey idiom), so headers
// and payload cannot drift apart.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, type DataTableColumn } from '@/components/common';
import type { PersonListItem } from '@/api/endpoints/peopleApi';
import { toDateInput } from '@/utils/date';
import i18n from '@/i18n/config';
import { PERSON_STATUS_META } from './personStatus';
import initialsOf from '@/utils/initials';

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Today · 06:30" / "Yesterday" / "2 days ago" / "3 weeks ago" / "1 month ago". */
const fmtLastLogin = (
  iso: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday) {
    const time = new Intl.DateTimeFormat(i18n.language, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    return t('people_last_today', { time });
  }
  if (d.getTime() >= startOfToday - DAY_MS) return t('people_last_yesterday');
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'always' });
  const days = Math.round((startOfToday - d.getTime()) / DAY_MS);
  if (days < 7) return rtf.format(-days, 'day');
  if (days < 30) return rtf.format(-Math.round(days / 7), 'week');
  if (days < 365) return rtf.format(-Math.round(days / 30), 'month');
  return rtf.format(-Math.round(days / 365), 'year');
};

const dash = <span className='people-table__dash'>—</span>;

/** ISO datetime → yyyy-mm-dd cell, dash when unset/unparseable. */
const dateCell = (iso: string) => {
  const day = toDateInput(iso);
  return day ? <span>{day}</span> : dash;
};

/** The optional profile columns — hidden by default (PeoplePage passes these
 *  to useColumnVisibility's defaultHidden), enabled via the Columns menu. */
export const PROFILE_COLUMN_KEYS = [
  'department',
  'employment',
  'idNumber',
  'idExpiry',
  'birthDate',
];

export const usePeopleColumns = (): DataTableColumn<PersonListItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'person',
      label: t('people_col_person'),
      render: (person) => (
        <span className='people-table__person'>
          <Avatar initials={initialsOf(person.fullName)} size={34} status='online' />
          <span className='people-table__identity'>
            <span className='people-table__name'>{person.fullName}</span>
            <span className='people-table__email'>{person.email}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'roles',
      label: t('people_col_roles'),
      render: (person) =>
        person.roles.length === 0 ? (
          dash
        ) : (
          <span className='people-table__chips people-table__chips--stack'>
            {person.roles.map((role) => (
              <Chip key={role.id} variant='info' iconStart='shield-check'>
                {role.name}
              </Chip>
            ))}
          </span>
        ),
    },
    {
      key: 'structure',
      label: t('people_col_structure'),
      render: (person) => {
        const shown = person.units.slice(0, 2);
        const extra = person.units.length - shown.length;
        return person.units.length === 0 ? (
          dash
        ) : (
          <span className='people-table__chips people-table__chips--stack'>
            {shown.map((unit, i) => (
              <Chip key={i} variant='neutral' iconStart={unit.levelIcon}>
                {unit.name}
              </Chip>
            ))}
            {extra > 0 && <span className='people-table__more'>+{extra}</span>}
          </span>
        );
      },
    },
    {
      key: 'projects',
      label: t('people_col_projects'),
      render: (person) => {
        const shown = person.projects.slice(0, 2);
        const extra = person.projects.length - shown.length;
        return person.projects.length === 0 ? (
          dash
        ) : (
          <span className='people-table__chips people-table__chips--stack'>
            {shown.map((project, i) => (
              <Chip key={i} variant='neutral' iconStart={project.icon}>
                {project.title}
              </Chip>
            ))}
            {extra > 0 && <span className='people-table__more'>+{extra}</span>}
          </span>
        );
      },
    },
    {
      key: 'phone',
      label: t('people_col_phone'),
      render: (person) =>
        person.phone ? (
          <span className='people-table__phone' dir='ltr'>
            {person.phone}
          </span>
        ) : (
          dash
        ),
    },
    // Optional profile columns (labels shared with the Add-person form, like
    // the import preview). All PROFILE_COLUMN_KEYS — hidden by default.
    {
      key: 'department',
      label: t('add_person_department'),
      render: (person) => (person.department ? <span>{person.department}</span> : dash),
    },
    {
      key: 'employment',
      label: t('add_person_emptype'),
      render: (person) => (person.employmentType ? <span>{person.employmentType}</span> : dash),
    },
    {
      key: 'idNumber',
      label: t('add_person_idnumber'),
      render: (person) => (person.personalIdNumber ? <span>{person.personalIdNumber}</span> : dash),
    },
    {
      key: 'idExpiry',
      label: t('add_person_idexpiry'),
      render: (person) => dateCell(person.personalIdExpiry),
    },
    {
      key: 'birthDate',
      label: t('add_person_birthdate'),
      render: (person) => dateCell(person.birthDate),
    },
    {
      key: 'groups',
      label: t('people_col_groups'),
      render: (person) => {
        const shown = person.groups.slice(0, 2);
        const extra = person.groups.length - shown.length;
        return person.groups.length === 0 ? (
          dash
        ) : (
          <span className='people-table__chips people-table__chips--stack'>
            {shown.map((group) => (
              <Chip key={group} variant='neutral' iconStart='users'>
                {group}
              </Chip>
            ))}
            {extra > 0 && <span className='people-table__more'>+{extra}</span>}
          </span>
        );
      },
    },
    {
      key: 'tags',
      label: t('people_col_tags'),
      render: (person) =>
        person.tags.length === 0 ? (
          dash
        ) : (
          <span className='people-table__chips people-table__chips--stack'>
            {person.tags.map((tag, i) => (
              <Chip key={i} variant='brand' iconStart='tag'>
                {tag.title}
              </Chip>
            ))}
          </span>
        ),
    },
    {
      key: 'nationality',
      label: t('people_col_nationality'),
      render: (person) => (person.nationality ? <span>{person.nationality}</span> : dash),
    },
    {
      key: 'status',
      label: t('people_col_status'),
      render: (person) => {
        const meta = PERSON_STATUS_META[person.status];
        return (
          <Chip variant={meta.variant} dot={meta.dot}>
            {t(meta.labelKey)}
          </Chip>
        );
      },
    },
    {
      key: 'lastLogin',
      label: t('people_col_last_login'),
      render: (person) => (
        <span className='people-table__login'>{fmtLastLogin(person.lastLoginDate, t)}</span>
      ),
    },
    {
      key: 'actions',
      label: t('people_col_actions'),
      align: 'end',
      // Placeholder — every tab that renders Actions swaps this render in
      // PeoplePage (PersonRowActions / GuestRowActions); only key/label/align
      // are consumed from here.
      render: () => null,
    },
  ];
};
